import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, Star, Flame, Medal, Crown, Target,
  ChevronRight, Loader2, TrendingUp, Award
} from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelColors = {
  bronze: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300', bar: 'bg-amber-500' },
  silver: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-300', border: 'border-gray-300', bar: 'bg-gray-400' },
  gold: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-400', bar: 'bg-yellow-500' },
  platinum: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-400', bar: 'bg-purple-500' }
};

const levelIcons = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💎'
};

export const GamificationPanel = ({ userId, language = 'es' }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showAllBadges, setShowAllBadges] = useState(false);

  useEffect(() => {
    fetchGamificationData();
  }, [userId]);

  const fetchGamificationData = async () => {
    try {
      const [statusRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/gamification/status?language=${language}`, {
          headers: { 'X-User-Id': userId }
        }),
        axios.get(`${API}/gamification/leaderboard?limit=10`)
      ]);
      
      setStatus(statusRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border/50">
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (!status) return null;

  const colors = levelColors[status.level?.id] || levelColors.bronze;
  const userRank = leaderboard.findIndex(u => u.user_id === userId) + 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="gamification-panel"
    >
      {/* Level Card */}
      <div className={`rounded-2xl p-6 border-2 ${colors.bg} ${colors.border}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <span className="text-4xl">{status.level?.icon || levelIcons[status.level?.id]}</span>
            <div>
              <p className="text-sm text-muted-foreground">{t('gamification.level')}</p>
              <p className={`text-2xl font-bold ${colors.text}`}>{status.level?.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">{t('gamification.points')}</p>
            <p className="text-3xl font-bold">{status.points}</p>
          </div>
        </div>

        {/* Progress to Next Level */}
        {status.next_level && (
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">
                {t('gamification.next_level')}: {status.next_level.name}
              </span>
              <span className={colors.text}>
                {status.next_level.points_remaining} pts
              </span>
            </div>
            <div className="h-3 bg-white/50 dark:bg-black/20 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${status.progress_to_next_level}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className={`h-full ${colors.bar} rounded-full`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border/50 text-center">
          <Flame className="w-6 h-6 mx-auto text-orange-500 mb-2" />
          <p className="text-2xl font-bold">{status.streak?.current || 0}</p>
          <p className="text-xs text-muted-foreground">{t('gamification.streak')}</p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border/50 text-center">
          <Medal className="w-6 h-6 mx-auto text-amber-500 mb-2" />
          <p className="text-2xl font-bold">{status.badges?.total_earned || 0}</p>
          <p className="text-xs text-muted-foreground">{t('gamification.badges')}</p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border/50 text-center">
          <Trophy className="w-6 h-6 mx-auto text-purple-500 mb-2" />
          <p className="text-2xl font-bold">#{userRank || '-'}</p>
          <p className="text-xs text-muted-foreground">{t('gamification.your_rank')}</p>
        </div>
      </div>

      {/* Badges */}
      <div className="bg-card rounded-2xl p-6 border border-border/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold">{t('gamification.badges')}</h3>
          </div>
          <span className="text-sm text-muted-foreground">
            {status.badges?.total_earned}/{status.badges?.total_available}
          </span>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
          {(status.badges?.earned || []).slice(0, showAllBadges ? undefined : 6).map((badge) => (
            <motion.div
              key={badge.id}
              whileHover={{ scale: 1.1 }}
              className="relative group"
              title={`${badge.name}: ${badge.description}`}
            >
              <div className="w-full aspect-square bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-xl flex items-center justify-center text-2xl">
                {badge.icon}
              </div>
              <div className="absolute inset-0 bg-black/80 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-1">
                <p className="text-white text-xs text-center">{badge.name}</p>
              </div>
            </motion.div>
          ))}
          
          {/* Placeholder for locked badges */}
          {Array.from({ length: Math.max(0, 6 - (status.badges?.total_earned || 0)) }).map((_, i) => (
            <div
              key={`locked-${i}`}
              className="w-full aspect-square bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center"
            >
              <span className="text-2xl opacity-30">🔒</span>
            </div>
          ))}
        </div>

        {status.badges?.total_earned > 6 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllBadges(!showAllBadges)}
            className="w-full mt-4"
          >
            {showAllBadges ? 'Ver menos' : `Ver todas (${status.badges.total_earned})`}
            <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${showAllBadges ? 'rotate-90' : ''}`} />
          </Button>
        )}
      </div>

      {/* Leaderboard */}
      <div className="bg-card rounded-2xl p-6 border border-border/50">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          <h3 className="font-semibold">{t('gamification.leaderboard')}</h3>
        </div>

        <div className="space-y-2">
          {leaderboard.slice(0, 5).map((user, idx) => (
            <div
              key={user.user_id}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                user.user_id === userId 
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800' 
                  : 'bg-gray-50 dark:bg-gray-800/50'
              }`}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                idx === 1 ? 'bg-gray-300 text-gray-700' :
                idx === 2 ? 'bg-amber-600 text-white' :
                'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {user.rank}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground">
                  {user.level_icon} {user.badges_count} badges
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold">{user.points}</p>
                <p className="text-xs text-muted-foreground">pts</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default GamificationPanel;
